const sortByOrder = (values) =>
  Object.entries(values).sort(([, a], [, b]) => (a.order || 0) - (b.order || 0));

const isValueDisplayType = (item, config) => (config.valueDisplayTypes || []).includes(item.type);

const isProgressionCheckbox = (item, config) => {
  const { stepType, settingsType } = config;
  return item.type === stepType || item.type === settingsType;
};

const getNextProgressionKey = (orderedEntries, fromIndex, config) => {
  for (let i = fromIndex + 1; i < orderedEntries.length; i += 1) {
    const [key, item] = orderedEntries[i];
    if (key === config.installAppKey) {
      continue;
    }
    if (isProgressionCheckbox(item, config)) {
      return key;
    }
  }
  return null;
};

const getProgressionKeys = (values, config) =>
  Object.keys(values).filter((key) => key !== config.installAppKey && isProgressionCheckbox(values[key], config));

export const extractChecklistState = (values) => {
  const checklist = {};
  Object.keys(values).forEach((key) => {
    if (typeof values[key]?.checked === "boolean") {
      checklist[key] = values[key].checked;
    }
  });
  return checklist;
};

export const applySavedChecklist = (defaultValues, savedChecklist = {}, config) => {
  const { installAppKey, adminScopesKey, settingsType } = config;

  if (!savedChecklist || !Object.keys(savedChecklist).length) {
    return defaultValues;
  }

  const result = Object.keys(defaultValues).reduce((acc, key) => {
    acc[key] = { ...defaultValues[key] };
    if (typeof savedChecklist[key] === "boolean") {
      acc[key].checked = savedChecklist[key];
    }
    return acc;
  }, {});

  let nextActiveAssigned = false;

  sortByOrder(result).forEach(([key, item]) => {
    if (key === installAppKey) {
      return;
    }

    if (isValueDisplayType(item, config)) {
      result[key] = { ...result[key], disable: false };
      return;
    }

    if (!isProgressionCheckbox(item, config)) {
      return;
    }

    if (!nextActiveAssigned) {
      if (!item.checked) {
        result[key] = { ...result[key], disable: false };
        nextActiveAssigned = true;
      } else {
        result[key] = { ...result[key], disable: false };
      }
    } else {
      result[key] = { ...result[key], checked: false, disable: true };
    }
  });

  if (result[adminScopesKey]?.checked) {
    Object.keys(result).forEach((key) => {
      if (result[key].type === settingsType) {
        result[key] = { ...result[key], disable: false };
      }
    });
  }

  const progressionKeys = getProgressionKeys(result, config);
  const checkedProgressionKeys = progressionKeys.filter((key) => result[key].checked);

  sortByOrder(result).forEach(([key, item]) => {
    if (!isValueDisplayType(item, config)) {
      return;
    }
    const ordered = sortByOrder(result);
    const idx = ordered.findIndex(([entryKey]) => entryKey === key);
    for (let i = idx - 1; i >= 0; i -= 1) {
      const [prevKey, prevItem] = ordered[i];
      if (isProgressionCheckbox(prevItem, config)) {
        if (result[prevKey].checked) {
          result[key] = { ...result[key], checked: true, disable: false };
        }
        break;
      }
    }
  });

  if (checkedProgressionKeys.length === progressionKeys.length) {
    result[installAppKey] = { ...result[installAppKey], disable: false };
  } else {
    result[installAppKey] = { ...result[installAppKey], checked: false, disable: true };
  }

  return result;
};

export const computeChecklistUpdate = (values, value, config) => {
  const { stepTypes, installAppKey, adminScopesKey, settingsType } = config;
  const updateData = {};

  if (value === installAppKey) {
    if (values[installAppKey]?.disable) {
      return updateData;
    }
    updateData[installAppKey] = {
      ...values[installAppKey],
      checked: !values[installAppKey].checked,
      disable: false,
    };
    return updateData;
  }

  const type = values[value]?.type;

  if (!stepTypes.includes(type) && type !== settingsType) {
    return updateData;
  }

  const orderedEntries = sortByOrder(values);
  const currentIdx = orderedEntries.findIndex(([key]) => key === value);

  if (type === settingsType) {
    updateData[value] = { ...values[value], checked: !values[value].checked, disable: false };
  } else if (values[value].checked) {
    orderedEntries.slice(currentIdx).forEach(([key, item]) => {
      if (stepTypes.includes(item.type) || key === installAppKey) {
        updateData[key] = {
          ...values[key],
          checked: false,
          disable: key === value ? false : true,
        };
      }
    });
  } else {
    updateData[value] = { ...values[value], checked: true, disable: false };

    const nextProgressionKey = getNextProgressionKey(orderedEntries, currentIdx, config);
    if (nextProgressionKey) {
      updateData[nextProgressionKey] = { ...values[nextProgressionKey], checked: false, disable: false };
    }

    for (let i = currentIdx + 1; i < orderedEntries.length; i += 1) {
      const [key, item] = orderedEntries[i];
      if (key === nextProgressionKey) {
        break;
      }
      if (isValueDisplayType(item, config)) {
        updateData[key] = { ...values[key], checked: true, disable: false };
      }
    }

    if (value === adminScopesKey) {
      Object.keys(values).forEach((item) => {
        if (values[item].type === settingsType) {
          updateData[item] = { ...values[item], disable: false };
        }
      });
    }
  }

  const updatedDataSet = { ...values, ...updateData };
  const progressionKeys = getProgressionKeys(updatedDataSet, config);
  const checkedProgressionKeys = progressionKeys.filter((key) => updatedDataSet[key].checked);

  if (checkedProgressionKeys.length === progressionKeys.length) {
    updateData[installAppKey] = { ...values[installAppKey], disable: false };
    if (value === installAppKey) {
      updateData[installAppKey] = {
        ...values[installAppKey],
        checked: !values[value].checked,
        disable: false,
      };
    }
  } else {
    updateData[installAppKey] = { ...values[installAppKey], checked: false, disable: true };
  }

  return updateData;
};

export default {
  applySavedChecklist,
  computeChecklistUpdate,
  extractChecklistState,
};
